/**
 * PositioningTab.tsx
 * ===================
 * 
 * Main component for the Positioning/Editorial Identity tab.
 * 
 * HISTORY:
 * - 2024-01: Initial implementation as monolithic component (~1440 lines)
 * - 2025-02: Major refactoring to respect 500-line limit:
 *   - Extracted onboarding wizard logic to usePositioningOnboarding.ts hook
 *   - Extracted chat view to PositioningChatView.tsx
 *   - Extracted text editor view to PositioningTextView.tsx  
 *   - Extracted version history view to PositioningHistoryView.tsx
 *   - Extracted header to PositioningHeader.tsx
 *   - Extracted toolbar to PositioningToolbar.tsx
 *   - Current size: ~405 lines
 * 
 * ARCHITECTURE:
 * - State management: modes (chat/text/history), chat messages, text editing, diffs, versions
 * - Onboarding flow: fully delegated to usePositioningOnboarding hook
 * - Views: delegated to specialized components (PositioningChatView, etc.)
 * - API calls: delegated to usePositioning hook
 * 
 * RELATED FILES:
 * - hooks/usePositioning.ts - API communication and main state
 * - hooks/usePositioningOnboarding.ts - Onboarding wizard flow and translations
 * - components/positioning/PositioningHeader.tsx - Header with mode switcher
 * - components/positioning/PositioningToolbar.tsx - Action buttons
 * - components/positioning/PositioningChatView.tsx - Chat interface for AI assistance
 * - components/positioning/PositioningTextView.tsx - Direct text editing
 * - components/positioning/PositioningHistoryView.tsx - Version history browser
 * - components/positioning/DiffView.tsx - Version comparison display
 */

import { useState, useEffect, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useAuthContext } from '../../../contexts/AuthContext';
import { Message, ChatRole } from '../../../types';
import { usePositioning } from '../../../hooks/usePositioning';
import { usePositioningOnboarding } from '../../../hooks/usePositioningOnboarding';
import { DiffView, type DiffChange } from '../positioning/DiffView';
import { PositioningHeader } from '../positioning/PositioningHeader';
import { PositioningChatView } from '../positioning/PositioningChatView';
import { PositioningTextView } from '../positioning/PositioningTextView';
import { PositioningHistoryView } from '../positioning/PositioningHistoryView';
import { PositioningToolbar } from '../positioning/PositioningToolbar';
import { BrandConfigContent } from '../positioning/BrandConfigContent';
import { OnboardingTranslations } from '../../../config/onboardingFlow';
import { Logger } from '../../../utils/logger';
import { toastService } from '../../../services/toastService';
import { useReadOnly } from '../../../hooks/useReadOnly';

interface PositioningPanelProps {
  systemPrompt: string;
  onUpdatePrompt: (prompt: string) => void;
  onClose: () => void;
}

export type Mode = 'chat' | 'text' | 'history' | 'brandConfig';

interface DiffState {
  visible: boolean;
  before: string;
  after: string;
  changes: DiffChange[];
  htmlDiff: string;
  warning?: string;
}

interface Version {
  version_id: string;
  content: string;
  created_at: string;
  source: string;
  summary?: string;
}

export function PositioningTab({ systemPrompt, onUpdatePrompt, onClose }: PositioningPanelProps) {
  const { t, language } = useLanguage();
  const readOnly = useReadOnly();
  const { currentSpace } = useAuthContext();

  // Mode state — V4-WIZARD-03 2026-05-29 : lit `?mode=brand` au mount pour
  // ouvrir directement la vue Charte graphique (signal envoyé par SetupWizard).
  // Le param est consommé puis retiré de l'URL pour ne pas re-trigger au
  // changement d'onglet.
  const [mode, setMode] = useState<Mode>(() => {
    try {
      if (typeof window === 'undefined') return 'chat';
      const params = new URLSearchParams(window.location.search);
      if (params.get('mode') === 'brand') return 'brandConfig';
    } catch (e) {
      // pas de window.location.search disponible (SSR ou jsdom partiel) → fallback
    }
    return 'chat';
  });

  useEffect(() => {
    // Nettoie le query param après lecture pour éviter qu'un re-mount ne
    // re-force le mode (ex: switch d'onglet → revient ensuite).
    if (mode === 'brandConfig') {
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.get('mode') === 'brand') {
          url.searchParams.delete('mode');
          window.history.replaceState({}, '', url.toString());
        }
      } catch (e) {
        // no-op
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Chat state
  const [input, setInput] = useState('');
  
  // Text mode state
  const [editedPrompt, setEditedPrompt] = useState(systemPrompt);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isTemplate, setIsTemplate] = useState(false);
  
  // Diff state
  const [diff, setDiff] = useState<DiffState>({
    visible: false,
    before: '',
    after: '',
    changes: [],
    htmlDiff: ''
  });
  
  // History state
  const [versions, setVersions] = useState<Version[]>([]);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  
  // Loading state for suggestion
  const [isLoadingSuggestion, setIsLoadingSuggestion] = useState(false);
  
  // Positioning hook
  const { 
    messages, 
    setMessages, 
    positioningContext, 
    isStreaming, 
    fetchPositioning, 
    sendMessageStream,
    extractFromUrl,
    applyChanges,
    getVersions,
    restoreVersion,
    updatePositioning,
    enrichPositioning,
    suggestChanges,
    isSavingPositioning
  } = usePositioning();

  // Onboarding hook
  const {
    onboardingActive,
    setOnboardingActive,
    currentStep,
    onboardingData,
    waitingForUrlInput,
    waitingForTextInput,
    currentInputPlaceholder,
    isExtracting,
    canGoBack,
    handleQuickReplySelect,
    handlePreviousStep,
    handleOnboardingInput,
    forceStartOnboarding,
    initializeOnboarding,
  } = usePositioningOnboarding({
    t: t as unknown as OnboardingTranslations,
    language,
    messages,
    setMessages,
    extractFromUrl,
    enrichPositioning,
    updatePositioning,
    onUpdatePrompt,
    setEditedPrompt,
    setIsTemplate,
  });

  // Fetch positioning data on mount AND when current space changes.
  // Fix V4-AGENCY-3 (2026-05-21) : on passe `currentSpace.id` pour éviter le
  // data leak où le backend retournait le positionnement d'un autre space.
  // Re-fetch sur changement de space pour que l'UI reflète la réalité du space
  // courant (sans reload manuel).
  useEffect(() => {
    if (!currentSpace?.id) return;
    // V4-FRONT-ANY-USAGE batch 6 2026-05-29
    fetchPositioning(currentSpace.id).then((data: { is_template?: boolean; positioning_context?: string } | null) => {
      Logger.log('[Positioning] API response:', {
        space_id: currentSpace.id,
        is_template: data?.is_template,
        hasContext: !!data?.positioning_context,
      });

      if (data?.is_template) {
        setIsTemplate(true);
        initializeOnboarding(true);
      } else {
        initializeOnboarding(false);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSpace?.id]);

  // Sync editedPrompt when positioningContext is loaded
  useEffect(() => {
    if (positioningContext) {
      setEditedPrompt(positioningContext);
    }
  }, [positioningContext]);

  // Also sync when systemPrompt prop changes
  useEffect(() => {
    if (systemPrompt && !positioningContext) {
      setEditedPrompt(systemPrompt);
    }
  }, [systemPrompt, positioningContext]);

  // Load versions when switching to history mode
  useEffect(() => {
    if (mode === 'history') {
      // V4-SETTINGS-LOCKEDSECTION-TS-LINT — forward-ref sûr (useEffect post-render ; handleLoadVersions défini plus bas).
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      handleLoadVersions();
    }
  }, [mode]);

  // ==========================================================================
  // HANDLERS
  // ==========================================================================

  const handleSend = async () => {
    if (!input.trim() || isStreaming) return;

    // Si on est en mode onboarding
    if (onboardingActive) {
      if (waitingForUrlInput || waitingForTextInput) {
        // UX backlog 9.1 : vider l'input IMMÉDIATEMENT (avant l'await) pour
        // éviter que l'utilisateur double-soumette pendant l'appel API qui peut
        // durer 5-10s (analyse URL + extraction LLM).
        const inputCopy = input;
        setInput('');
        await handleOnboardingInput(inputCopy);
        return;
      }
      return;
    }

    // Mode normal (hors onboarding) - Appel API
    const userMessage: Message = {
      id: Date.now().toString(),
      role: ChatRole.USER,
      content: input.trim(),
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    const userInput = input.trim();
    setInput('');

    // Create placeholder for streaming response
    const assistantId = (Date.now() + 1).toString();
    setMessages(prev => [...prev, { id: assistantId, role: ChatRole.ASSISTANT, content: '', isTyping: true }]);

    await sendMessageStream(
      userInput,
      messages,
      (chunk) => {
        setMessages(prev =>
          prev.map(msg =>
            msg.id === assistantId
              ? { ...msg, content: msg.content + chunk }
              : msg
          )
        );
      },
      () => {
        setMessages(prev =>
          prev.map(msg =>
            msg.id === assistantId
              ? { ...msg, isTyping: false }
              : msg
          )
        );
      },
      (error) => {
        setMessages(prev =>
          prev.map(msg =>
            msg.id === assistantId
              ? { ...msg, content: `Erreur: ${error.message}`, isTyping: false }
              : msg
          )
        );
      },
      // V4-AGENCY-3-OTHER (2026-05-21) : cible explicite du space courant
      currentSpace?.id,
    );
  };

  const handleSaveText = async () => {
    try {
      await updatePositioning(editedPrompt, 'manual', currentSpace?.id);
      onUpdatePrompt(editedPrompt);
      setHasUnsavedChanges(false);
      setIsTemplate(false);
      toastService.success(t.toasts?.positioning?.saveSuccess || 'Positionnement sauvegardé');
      
      // Refresh versions
      setTimeout(async () => {
        try {
          const result = await getVersions(currentSpace?.id);
          setVersions(result.versions || []);
        } catch (e) {
          Logger.error('Failed to refresh versions', e);
        }
      }, 500);
    } catch (error) {
      toastService.error(t.toasts?.positioning?.saveError || 'Erreur lors de la sauvegarde');
    }
  };

  const handleAcceptDiff = async (finalResult: string) => {
    try {
      await applyChanges(finalResult, 'ai_suggestion', currentSpace?.id);
      setEditedPrompt(finalResult);
      onUpdatePrompt(finalResult);
      setDiff({ ...diff, visible: false });
      toastService.success(t.positioning?.changesApplied || 'Modifications appliquées');
      
      // Refresh versions
      setTimeout(async () => {
        try {
          const result = await getVersions(currentSpace?.id);
          setVersions(result.versions || []);
        } catch (e) {
          Logger.error('Failed to refresh versions', e);
        }
      }, 500);
    } catch (error) {
      toastService.error(t.positioning?.applyError || 'Erreur lors de l\'application');
    }
  };

  const handleRejectDiff = () => {
    setDiff({ ...diff, visible: false });
  };

  /**
   * Appliquer un positionnement proposé par le chat
   */
  const handleApplyFromChat = async (newPositioning: string) => {
    try {
      await applyChanges(newPositioning, 'chat_suggestion', currentSpace?.id);
      setEditedPrompt(newPositioning);
      onUpdatePrompt(newPositioning);
      setIsTemplate(false);
      toastService.success(t.positioning?.positioningApplied || 'Positionnement appliqué avec succès');
      
      // Refresh versions
      setTimeout(async () => {
        try {
          const result = await getVersions(currentSpace?.id);
          setVersions(result.versions || []);
        } catch (e) {
          Logger.error('Failed to refresh versions', e);
        }
      }, 500);
    } catch (error) {
      toastService.error(t.positioning?.applyError || 'Erreur lors de l\'application du positionnement');
    }
  };

  /**
   * Appliquer une suggestion de modification avec preview du diff
   * Cette fonction appelle l'API /suggest-changes pour obtenir le diff,
   * puis affiche le DiffView pour que l'utilisateur valide avant d'appliquer.
   */
  const handleApplySuggestion = async (instruction: string) => {
    // Éviter les double-clics
    if (isLoadingSuggestion) return;
    
    setIsLoadingSuggestion(true);
    try {
      // Appeler l'API pour obtenir les modifications suggérées
      const result = await suggestChanges(instruction, positioningContext || editedPrompt, currentSpace?.id);
      
      // Le backend retourne directement before/after/changes/html_diff (pas dans diff_result)
      if (result?.after) {
        // Afficher le DiffView avec les modifications proposées
        setDiff({
          visible: true,
          before: result.before || positioningContext || editedPrompt,
          after: result.after,
          changes: result.changes || [],
          htmlDiff: result.html_diff || '',
          warning: result.warning
        });
        toastService.success(t.positioning?.suggestionReady || 'Modifications prêtes - vérifiez et validez');
      } else if (result?.new_positioning) {
        // Fallback si format legacy avec new_positioning
        setDiff({
          visible: true,
          before: positioningContext || editedPrompt,
          after: result.new_positioning,
          changes: [],
          htmlDiff: '',
          warning: result.warning
        });
        toastService.success(t.positioning?.suggestionReady || 'Modifications prêtes - vérifiez et validez');
      } else {
        toastService.error(t.positioning?.noSuggestion || 'Aucune modification suggérée');
      }
    } catch (error) {
      Logger.error('Failed to apply suggestion:', error);
      toastService.error(t.positioning?.suggestionError || 'Erreur lors de la génération des modifications');
    } finally {
      setIsLoadingSuggestion(false);
    }
  };

  const handleLoadVersions = async () => {
    setIsLoadingVersions(true);
    try {
      const result = await getVersions(currentSpace?.id);
      setVersions(result.versions || []);
    } catch (error) {
      toastService.error(t.toasts?.error || 'Erreur lors du chargement des versions');
    } finally {
      setIsLoadingVersions(false);
    }
  };

  const handleRestoreVersion = async (versionId: string) => {
    try {
      const result = await restoreVersion(versionId, currentSpace?.id);
      if (result.positioning_context) {
        setEditedPrompt(result.positioning_context);
        onUpdatePrompt(result.positioning_context);
        toastService.success(t.positioning?.versionRestored || 'Version restaurée');
        setMode('text');
      }
    } catch (error) {
      toastService.error(t.toasts?.error || 'Erreur lors de la restauration');
    }
  };

  // ==========================================================================
  // RENDER TOOLBAR
  // ==========================================================================

  const renderToolbar = () => (
    <PositioningToolbar
      mode={mode}
      setMode={setMode}
      hasUnsavedChanges={hasUnsavedChanges}
      positioningContext={positioningContext}
      setEditedPrompt={setEditedPrompt}
      setHasUnsavedChanges={setHasUnsavedChanges}
      onSave={handleSaveText}
      isSaving={isSavingPositioning}
      t={t}
      readOnly={readOnly}
    />
  );

  // ==========================================================================
  // MAIN RENDER
  // ==========================================================================

  return (
    // Fix UX 20-05 — `min-height: 0` (inline) indispensable pour que les enfants flex
    // se compressent et que la zone d'input reste visible peu importe le zoom navigateur.
    <div className="flex flex-col h-full" style={{ minHeight: 0 }}>
      {/* Header */}
      <PositioningHeader
        mode={mode}
        setMode={setMode}
        onClose={onClose}
        onboardingActive={onboardingActive}
        forceStartOnboarding={forceStartOnboarding}
        t={t}
      />

      {/* Diff View */}
      {diff.visible && (
        <div className="p-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <DiffView
            before={diff.before}
            after={diff.after}
            changes={diff.changes}
            htmlDiff={diff.htmlDiff}
            onAccept={handleAcceptDiff}
            onReject={handleRejectDiff}
            title="Modifications proposées"
            warning={diff.warning}
          />
        </div>
      )}

      {/* Content — UX backlog 5.2 : transition fade entre les modes (chat/text/brandConfig/history). */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={mode}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="flex-1 flex flex-col"
          style={{ minHeight: 0 }}
        >
          {mode === 'chat' ? (
            <PositioningChatView
              messages={messages}
              input={input}
              setInput={setInput}
              isStreaming={isStreaming}
              isExtracting={isExtracting}
              isLoadingSuggestion={isLoadingSuggestion}
              onboardingActive={onboardingActive}
              currentStep={currentStep}
              onboardingData={onboardingData}
              waitingForUrlInput={waitingForUrlInput}
              waitingForTextInput={waitingForTextInput}
              currentInputPlaceholder={currentInputPlaceholder}
              canGoBack={canGoBack}
              handleSend={handleSend}
              handleQuickReplySelect={handleQuickReplySelect}
              handlePreviousStep={handlePreviousStep}
              forceStartOnboarding={forceStartOnboarding}
              onApplyPositioning={handleApplyFromChat}
              onApplySuggestion={handleApplySuggestion}
              isTemplate={isTemplate}
              t={t}
            />
          ) : mode === 'history' ? (
            <PositioningHistoryView
              versions={versions}
              isLoadingVersions={isLoadingVersions}
              onRefresh={handleLoadVersions}
              onRestore={handleRestoreVersion}
              t={t}
            />
          ) : mode === 'brandConfig' ? (
            // Chantier P 2026-05 — vue Charte graphique inline (3e toggle).
            <div className="flex-1 overflow-y-auto p-6">
              <BrandConfigContent />
            </div>
          ) : (
            <PositioningTextView
              editedPrompt={editedPrompt}
              setEditedPrompt={setEditedPrompt}
              positioningContext={positioningContext}
              setHasUnsavedChanges={setHasUnsavedChanges}
              isTemplate={isTemplate}
              t={t}
              renderToolbar={renderToolbar}
              readOnly={readOnly}
            />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default PositioningTab;
